import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Copy, FileDown, RefreshCw, LogOut, AlertTriangle, ChevronDown, ChevronUp, Send, Library, FileText, MailPlus, Search, X, Mail, Undo2 } from 'lucide-react';
import { TESTIDS } from '@/constants/testIds';
import { apiAdmin, setAdminAuth, clearAdminAuth, getAdminAuth, API_BASE } from '@/lib/api';
import { DocumentLibrary } from '@/components/DocumentLibrary';
import { EmailActivity } from '@/components/EmailActivity';
import { toast } from 'sonner';

function LoginScreen({ onSuccess }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    setAdminAuth(u, p);
    try {
      const r = await apiAdmin.get('/admin/me');
      onSuccess(r.data);
    } catch (e) {
      clearAdminAuth();
      setErr('Invalid username or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center px-6">
      <form
        data-testid={TESTIDS.adminLoginForm}
        onSubmit={submit}
        className="relative z-10 w-full max-w-md pnc-card rounded-lg p-10"
      >
        <div className="text-center mb-8">
          <img src="/pnc-logo-white.png" alt="PNC UNIQUE LTD" className="h-14 w-auto mx-auto mb-3" />
          <p className="text-[10px] uppercase tracking-[0.28em] text-[var(--muted)] font-semibold">
            Contract E-Signature Portal
          </p>
        </div>
        <p className="field-label mb-2">Admin console</p>
        <h2 className="serif text-3xl mb-6">Sign in</h2>
        <div className="space-y-4">
          <div>
            <label className="field-label block mb-1.5">Username</label>
            <input
              data-testid={TESTIDS.adminLoginUsername}
              value={u}
              onChange={(e) => setU(e.target.value)}
              className="input-bare"
              required
              autoComplete="username"
            />
          </div>
          <div>
            <label className="field-label block mb-1.5">Password</label>
            <input
              data-testid={TESTIDS.adminLoginPassword}
              type="password"
              value={p}
              onChange={(e) => setP(e.target.value)}
              className="input-bare"
              required
              autoComplete="current-password"
            />
          </div>
        </div>
        {err && (
          <p data-testid={TESTIDS.adminLoginError} className="mt-4 text-sm text-rose-400">{err}</p>
        )}
        <button
          data-testid={TESTIDS.adminLoginSubmit}
          type="submit"
          disabled={loading}
          className="btn-ink w-full mt-6 py-3 rounded-md text-sm font-bold uppercase tracking-[0.08em]"
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
        <Link to="/" className="block text-center text-xs text-[var(--muted)] mt-6 hover:text-white transition-colors">← Back</Link>
      </form>
    </div>
  );
}

function OptOutPill({ record }) {
  // Statuses for the 48-hour declaration: Pending / Opted Out / Not Opted Out.
  // Both completed states are equal-standing — Option B is never a rejection.
  if (record.status === 'signed') {
    const optedOut = record.optout_choice === 'opt_out';
    return (
      <span
        data-testid="optout-status-pill"
        className={`inline-block px-2 py-0.5 text-[11px] rounded-full font-semibold uppercase tracking-wider ${
          optedOut ? 'bg-sky-500/15 text-sky-300' : 'bg-emerald-500/15 text-emerald-300'}`}
      >
        {optedOut ? 'Opted Out' : 'Not Opted Out'}
      </span>
    );
  }
  if (record.status === 'sent' || record.status === 'queued' || record.status === 'opened') {
    return (
      <span
        data-testid="optout-status-pill"
        className="inline-block px-2 py-0.5 text-[11px] rounded-full font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-300"
      >
        Pending
      </span>
    );
  }
  return <StatusPill status={record.status} />;
}

function StatusPill({ status }) {
  const map = {
    queued: 'bg-[#2A2A2A] text-[#D4D4D4]',
    sent: 'bg-amber-500/15 text-amber-300',
    opened: 'bg-blue-500/15 text-blue-300',
    signed: 'bg-emerald-500/15 text-emerald-300',
    declined: 'bg-rose-500/15 text-rose-300',
    expired: 'bg-[#2A2A2A] text-[#8A8A8A]',
    cancelled: 'bg-[#2A2A2A] text-[#8A8A8A]',
    failed: 'bg-red-500/15 text-red-300',
  };
  const cls = map[status] || 'bg-[#2A2A2A] text-[#D4D4D4]';
  return (
    <span data-testid={TESTIDS.contractRowStatus} className={`inline-block px-2 py-0.5 text-[11px] rounded-full font-semibold uppercase tracking-wider ${cls}`}>
      {status}
    </span>
  );
}

const SERVICE_LABEL = {
  contract: 'Contract',
  invoice: 'Weekly Invoice Creation',
  insurance: 'Insurance Cover',
  health_safety: 'Health & Safety',
  medical: 'Medical History & HAVS',
  optout_48h: '48-Hour Working Time Declaration',
  custom: 'Document',
};

function TestModeBanner({ active, email }) {
  if (!active) return null;
  return (
    <div data-testid={TESTIDS.testModeBanner} className="bg-amber-500/10 border-y border-amber-500/30 relative z-10">
      <div className="max-w-6xl mx-auto px-6 py-3 text-xs text-amber-200 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold uppercase tracking-wider">Test mode</span>
        <span className="opacity-90">All outgoing invitations and signed PDFs are redirected to <strong>{email || 'TEST_REDIRECT_EMAIL'}</strong>. No real employees will receive emails. Clear <code className="bg-amber-500/15 px-1 rounded">TEST_REDIRECT_EMAIL</code> in <code className="bg-amber-500/15 px-1 rounded">backend/.env</code> to go live.</span>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ target, deleting, onCancel, onConfirm }) {
  if (!target) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-6">
      <div
        data-testid={TESTIDS.deleteConfirmDialog}
        role="dialog"
        aria-modal="true"
        className="bg-[#1E1E1E] border border-[var(--line)] rounded-lg shadow-2xl max-w-md w-full p-7"
      >
        <div className="w-11 h-11 rounded-full bg-rose-500/15 text-rose-400 flex items-center justify-center mb-4">
          <AlertTriangle size={20} strokeWidth={2} />
        </div>
        <p className="serif text-2xl mb-2">Delete this record?</p>
        <p className="text-sm text-[var(--muted)] leading-relaxed mb-2">
          You are about to permanently delete the record for{' '}
          <strong className="text-[var(--text)] break-all">{target.employee_email}</strong>
          {' '}({SERVICE_LABEL[target.service_type] || 'Contract'}).
        </p>
        <p className="text-sm text-[var(--muted)] leading-relaxed mb-6">
          The signing link will stop working and any stored signed PDF will also be deleted.
          This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <button
            data-testid={TESTIDS.deleteCancelBtn}
            onClick={onCancel}
            disabled={deleting}
            className="btn-ghost px-4 py-2 rounded-md text-sm font-semibold"
            type="button"
          >
            Cancel
          </button>
          <button
            data-testid={TESTIDS.deleteConfirmBtn}
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-50"
            type="button"
          >
            {deleting ? 'Deleting…' : 'Yes, delete record'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminConsole({ session, onLogout }) {
  const [tab, setTab] = useState('send');
  const [emails, setEmails] = useState('');
  const [contractType, setContractType] = useState('unique');
  const [availableDocs, setAvailableDocs] = useState([]);
  const [docsOpen, setDocsOpen] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [error, setError] = useState('');
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resendingToken, setResendingToken] = useState(null);
  const [search, setSearch] = useState('');
  const [emailJobs, setEmailJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [undoingBatch, setUndoingBatch] = useState(false);

  const loadJobs = async () => {
    try {
      const r = await apiAdmin.get('/admin/email-jobs?limit=200');
      setEmailJobs(r.data);
    } catch {
      /* silent — the Email Activity tab has its own refresh button */
    } finally {
      setJobsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadJobs(); }, []);

  // Poll the outbox: fast while emails are scheduled/sending or the tab is
  // open, slow otherwise (keeps the pending badge honest).
  const hasActiveJobs = emailJobs.some((j) => j.status === 'pending' || j.status === 'sending');
  useEffect(() => {
    const ms = (hasActiveJobs || tab === 'activity') ? 5000 : 30000;
    const id = setInterval(loadJobs, ms);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveJobs, tab]);

  const pendingJobCount = emailJobs.filter((j) => j.status === 'pending').length;

  // Load the active document list for the selected company (dynamic library)
  useEffect(() => {
    let cancelled = false;
    setSelectedDocIds([]);
    setLoadingDocs(true);
    apiAdmin.get(`/admin/documents?company=${contractType}&active_only=true`)
      .then((r) => { if (!cancelled) setAvailableDocs(r.data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load documents.'); })
      .finally(() => { if (!cancelled) setLoadingDocs(false); });
    return () => { cancelled = true; };
  }, [contractType]);

  const toggleDoc = (id) => {
    setSelectedDocIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  // Build a tiny signature of the list so we can skip setState (and the
  // re-render flash) when nothing meaningful has changed between polls.
  const listSig = (xs) =>
    xs.map((x) => `${x.token}:${x.status}:${x.signed_at || ''}`).join('|');

  const loadList = async ({ silent = false } = {}) => {
    if (!silent) setLoadingList(true);
    try {
      const r = await apiAdmin.get('/admin/contracts');
      setList((prev) => (listSig(prev) === listSig(r.data) ? prev : r.data));
    } catch (e) {
      if (!silent) toast.error('Failed to load contracts');
    } finally {
      if (!silent) setLoadingList(false);
    }
  };

  // Initial fetch with the visible spinner
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadList(); }, []);

  // Auto-refresh after a batch send — exactly ONE interval lives per
  // batch_id. Stops as soon as every watched token has left the
  // 'queued' state, OR after a 30 s hard cap.
  useEffect(() => {
    if (!lastBatch || !Array.isArray(lastBatch.tokens) || lastBatch.tokens.length === 0) return;
    let cancelled = false;
    const watched = new Set(lastBatch.tokens);
    let interval;
    let timeout;
    const stop = () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await apiAdmin.get('/admin/contracts');
        if (cancelled) return;
        setList((prev) => (listSig(prev) === listSig(r.data) ? prev : r.data));
        const stillPending = r.data.some(
          (c) => watched.has(c.token) && c.status === 'queued',
        );
        if (!stillPending) stop();
      } catch {
        /* swallow polling errors — manual refresh button surfaces them */
      }
    };
    timeout = setTimeout(() => {
      tick();
      interval = setInterval(tick, 3000);
      timeout = setTimeout(stop, 30000);
    }, 1500);
    return () => {
      cancelled = true;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastBatch?.batch_id]);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLastBatch(null);
    if (selectedDocIds.length === 0) {
      setError('Select at least one document to send.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await apiAdmin.post('/admin/contracts/send_batch', {
        emails_raw: emails,
        contract_type: contractType,
        document_ids: selectedDocIds,
      });
      setLastBatch(r.data);
      setEmails('');
      loadJobs();
      const d = r.data.delay_minutes;
      toast.success(
        d > 0
          ? `${r.data.total} email${r.data.total === 1 ? '' : 's'} scheduled — you have ${d} minute${d === 1 ? '' : 's'} to undo`
          : `${r.data.total} email${r.data.total === 1 ? '' : 's'} sending now`,
      );
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to queue signing links.');
    } finally {
      setSubmitting(false);
    }
  };

  const resendInvitation = async (c) => {
    setResendingToken(c.token);
    try {
      const r = await apiAdmin.post(`/admin/contracts/${c.token}/resend`);
      const d = r.data.delay_minutes;
      toast.success(
        d > 0
          ? `Re-send to ${c.employee_email} scheduled — undo within ${d} minute${d === 1 ? '' : 's'} in Email Activity.`
          : `Re-send to ${c.employee_email} is on its way.`,
      );
      loadJobs();
      loadList({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed.');
    } finally {
      setResendingToken(null);
    }
  };

  const undoLastBatch = async () => {
    if (!lastBatch?.batch_id) return;
    setUndoingBatch(true);
    try {
      const r = await apiAdmin.post('/admin/email-jobs/cancel-batch', { batch_id: lastBatch.batch_id });
      toast.success(
        `${r.data.cancelled} email${r.data.cancelled === 1 ? '' : 's'} cancelled.` +
        (r.data.too_late ? ` ${r.data.too_late} had already sent.` : ''),
      );
      setLastBatch((prev) => (prev ? { ...prev, undone: true, undone_count: r.data.cancelled } : prev));
      loadJobs();
      loadList({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel this batch.');
    } finally {
      setUndoingBatch(false);
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/contract-sign/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Signing link copied.');
    } catch {
      toast.error('Could not copy.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiAdmin.delete(`/admin/contracts/${deleteTarget.token}`);
      setList((prev) => prev.filter((c) => c.token !== deleteTarget.token));
      toast.success(`Record for ${deleteTarget.employee_email} deleted.`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete the record.');
    } finally {
      setDeleting(false);
    }
  };

  const downloadMedicalPdf = async (c) => {
    try {
      const r = await apiAdmin.get(`/admin/contracts/${c.token}/medical-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Could not open the medical PDF.');
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await apiAdmin.get('/admin/contracts.csv', { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pnc_contracts_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success('CSV exported.');
    } catch {
      toast.error('Could not export CSV.');
    }
  };

  const parsedCount = (() => {
    const tokens = emails.split(/[\s,;]+/).filter((t) => t.includes('@'));
    return tokens.length;
  })();

  const q = search.trim().toLowerCase();
  const visibleList = q
    ? list.filter((c) =>
        [c.employee_email, c.contractor_name, c.contractor_company, c.document_name]
          .some((v) => (v || '').toLowerCase().includes(q)))
    : list;

  return (
    <div data-testid={TESTIDS.adminPage} className="min-h-screen grain">
      <header className="relative border-b border-[var(--line)] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex">
            <img src="/pnc-logo-white.png" alt="PNC UNIQUE LTD" className="h-9 w-auto" />
          </Link>
          <button
            data-testid={TESTIDS.adminLogout}
            onClick={onLogout}
            className="inline-flex items-center gap-1.5 text-xs text-[var(--muted)] hover:text-white transition-colors"
          >
            <LogOut size={13} strokeWidth={2} /> Sign out
          </button>
        </div>
      </header>

      <TestModeBanner active={session?.test_redirect_active} email={session?.test_redirect_email} />

      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-8">
        <div className="inline-flex gap-1 p-1 rounded-lg border border-[var(--line)] bg-[#141414]">
          <button
            data-testid={TESTIDS.adminTabSend}
            onClick={() => setTab('send')}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-md text-xs font-bold uppercase tracking-[0.08em] transition-colors ${
              tab === 'send' ? 'bg-[var(--silver)] text-[#0A0A0A]' : 'text-[var(--muted)] hover:text-white'}`}
          >
            <Send size={13} strokeWidth={2} /> Send &amp; Track
          </button>
          <button
            data-testid="admin-tab-activity"
            onClick={() => setTab('activity')}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-md text-xs font-bold uppercase tracking-[0.08em] transition-colors ${
              tab === 'activity' ? 'bg-[var(--silver)] text-[#0A0A0A]' : 'text-[var(--muted)] hover:text-white'}`}
          >
            <Mail size={13} strokeWidth={2} /> Email Activity
            {pendingJobCount > 0 && (
              <span
                data-testid="email-pending-badge"
                className={`px-1.5 py-0.5 text-[10px] rounded-full font-bold ${
                  tab === 'activity' ? 'bg-[#0A0A0A] text-amber-300' : 'bg-amber-500/20 text-amber-300'}`}
              >
                {pendingJobCount}
              </span>
            )}
          </button>
          <button
            data-testid={TESTIDS.adminTabLibrary}
            onClick={() => setTab('library')}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-md text-xs font-bold uppercase tracking-[0.08em] transition-colors ${
              tab === 'library' ? 'bg-[var(--silver)] text-[#0A0A0A]' : 'text-[var(--muted)] hover:text-white'}`}
          >
            <Library size={13} strokeWidth={2} /> Document Library
          </button>
        </div>
      </div>

      <main className="relative z-10 max-w-6xl mx-auto px-6 py-10">
      {tab === 'library' ? (
        <DocumentLibrary />
      ) : tab === 'activity' ? (
        <EmailActivity jobs={emailJobs} loading={jobsLoading} onRefresh={loadJobs} />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        {/* Send batch */}
        <section className="lg:col-span-2">
          <p className="field-label mb-3">Step 01</p>
          <h2 className="serif text-3xl sm:text-4xl leading-tight mb-2">Send documents</h2>
          <p className="text-sm text-[var(--muted)] mb-7">
            Pick a company, choose which documents to send, then paste recipient emails.
            Each recipient gets a separate email and tracking row per document.
          </p>

          <form data-testid={TESTIDS.sendBatchForm} onSubmit={submit} className="pnc-card rounded-lg p-7 space-y-5">
            <div>
              <label className="field-label block mb-2">Company</label>
              <div className="grid grid-cols-1 gap-2">
                {[
                  { value: 'unique', label: 'PNC UNIQUE', tid: TESTIDS.sendBatchContractTypeUnique },
                  { value: 'smart', label: 'PNC SMART SOLUTIONS', tid: TESTIDS.sendBatchContractTypeSmart },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    data-testid={opt.tid}
                    className={`flex items-center gap-2.5 px-3.5 py-3 rounded-md border cursor-pointer transition-colors ${
                      contractType === opt.value
                        ? 'border-[var(--silver)] bg-[#161616]'
                        : 'border-[var(--line)] hover:border-[#4A4A4A]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="contract_type"
                      value={opt.value}
                      checked={contractType === opt.value}
                      onChange={() => setContractType(opt.value)}
                      className="accent-[var(--silver)]"
                    />
                    <span className="text-sm font-semibold tracking-wide">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <button
                type="button"
                data-testid={TESTIDS.docShowAvailableBtn}
                onClick={() => setDocsOpen((v) => !v)}
                className="btn-ghost w-full flex items-center justify-between px-4 py-3 rounded-md text-sm font-semibold"
              >
                <span className="inline-flex items-center gap-2">
                  <FileText size={14} strokeWidth={2} className="text-[var(--silver)]" />
                  {docsOpen ? 'Hide Available Documents' : 'Show Available Documents'}
                </span>
                <span className="inline-flex items-center gap-2">
                  {selectedDocIds.length > 0 && (
                    <span className="px-2 py-0.5 text-[10px] rounded-full font-bold bg-[var(--silver)] text-[#0A0A0A]">
                      {selectedDocIds.length} selected
                    </span>
                  )}
                  {docsOpen ? <ChevronUp size={15} strokeWidth={2} /> : <ChevronDown size={15} strokeWidth={2} />}
                </span>
              </button>

              {docsOpen && (
                <div data-testid={TESTIDS.docAvailableList} className="mt-2 border border-[var(--line)] rounded-md divide-y divide-[var(--line)] max-h-72 overflow-y-auto">
                  {loadingDocs ? (
                    <p className="p-4 text-xs text-[var(--muted)]">Loading documents…</p>
                  ) : availableDocs.length === 0 ? (
                    <p className="p-4 text-xs text-[var(--muted)]">
                      No active documents for this company. Add one in the Document Library tab.
                    </p>
                  ) : (
                    availableDocs.map((d) => (
                      <label
                        key={d.id}
                        data-testid={TESTIDS.docCheckbox}
                        className={`flex items-start gap-3 px-3.5 py-3 cursor-pointer transition-colors ${
                          selectedDocIds.includes(d.id) ? 'bg-[#161616]' : 'hover:bg-[#141414]'}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDocIds.includes(d.id)}
                          onChange={() => toggleDoc(d.id)}
                          className="accent-[var(--silver)] mt-0.5"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold">{d.name}</span>
                          {d.description && <span className="block text-[11px] text-[var(--muted)] mt-0.5">{d.description}</span>}
                        </span>
                        {d.kind === 'builtin' && (
                          <span className="ml-auto shrink-0 px-2 py-0.5 text-[9px] rounded-full font-semibold uppercase tracking-wider bg-[#2A2A2A] text-[#D4D4D4]">System</span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="field-label block mb-1.5">Employee emails</label>
              <textarea
                data-testid={TESTIDS.sendBatchEmails}
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder={"person1@email.com\nperson2@email.com, person3@email.com\nperson4@email.com"}
                rows={9}
                className="input-bare font-mono text-[13px] leading-relaxed"
                required
              />
              <div className="flex items-center justify-between mt-2 text-[11px] text-[var(--muted)]">
                <span>One per line, or comma/semicolon-separated. Up to 200 per batch.</span>
                <span className="font-semibold text-[var(--text)]">{parsedCount} detected</span>
              </div>
            </div>

            {error && (
              <div data-testid={TESTIDS.sendBatchError} className="alert-error text-sm rounded p-3">
                {error}
              </div>
            )}

            <button
              data-testid={TESTIDS.sendBatchSubmit}
              type="submit"
              disabled={submitting || !emails.trim() || selectedDocIds.length === 0}
              className="btn-ink w-full py-3 rounded-md text-sm font-bold uppercase tracking-[0.08em]"
            >
              {submitting
                ? 'Queueing links…'
                : selectedDocIds.length > 1
                  ? `Send ${selectedDocIds.length} documents`
                  : 'Send signing links'}
            </button>
          </form>

          {lastBatch && (
            <div data-testid={TESTIDS.sendBatchSuccess} className="mt-5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-5">
              <p data-testid={TESTIDS.batchSummary} className="font-semibold text-emerald-300 mb-1">
                Batch queued · {lastBatch.recipients ?? lastBatch.total} recipient{(lastBatch.recipients ?? lastBatch.total) === 1 ? '' : 's'}
                {lastBatch.documents?.length ? ` × ${lastBatch.documents.length} document${lastBatch.documents.length === 1 ? '' : 's'} = ${lastBatch.total} emails` : ''}
              </p>
              {lastBatch.documents?.length > 0 && (
                <p className="text-[12px] text-emerald-200/80 mb-1">{lastBatch.documents.map((d) => d.name).join(' · ')}</p>
              )}
              {lastBatch.undone ? (
                <p data-testid="batch-undone-note" className="text-[12px] text-rose-300 font-semibold">
                  Undone — {lastBatch.undone_count} email{lastBatch.undone_count === 1 ? '' : 's'} cancelled before sending.
                </p>
              ) : lastBatch.delay_minutes > 0 ? (
                <>
                  <p className="text-[12px] text-emerald-200/80">
                    Emails will send in <strong>{lastBatch.delay_minutes} minute{lastBatch.delay_minutes === 1 ? '' : 's'}</strong> — until
                    then you can still stop them. Batch ID <code className="bg-emerald-500/15 px-1.5 rounded">{lastBatch.batch_id}</code>.
                  </p>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <button
                      data-testid="batch-undo-send-btn"
                      type="button"
                      onClick={undoLastBatch}
                      disabled={undoingBatch}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-bold uppercase tracking-[0.06em] bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-50"
                    >
                      <Undo2 size={13} strokeWidth={2} />
                      {undoingBatch ? 'Cancelling…' : 'Undo send'}
                    </button>
                    <button
                      data-testid="batch-view-activity-btn"
                      type="button"
                      onClick={() => setTab('activity')}
                      className="btn-ghost inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold"
                    >
                      <Mail size={13} strokeWidth={2} /> View in Email Activity
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-[12px] text-emerald-200/80">Batch ID <code className="bg-emerald-500/15 px-1.5 rounded">{lastBatch.batch_id}</code>. Emails are sending now — refresh the activity list for live status.</p>
              )}
              {lastBatch.invalid_inputs?.length > 0 && (
                <p className="text-[12px] mt-2 text-rose-300">Skipped {lastBatch.invalid_inputs.length} invalid entries: {lastBatch.invalid_inputs.join(', ')}</p>
              )}
              {lastBatch.test_redirect_active && (
                <p className="text-[12px] mt-2 text-emerald-200/80">Test mode — emails will arrive at <strong>{lastBatch.test_redirect_email}</strong>.</p>
              )}
            </div>
          )}
        </section>

        {/* List */}
        <section className="lg:col-span-3">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="field-label mb-3">Activity</p>
              <h2 className="serif text-3xl sm:text-4xl leading-tight">Recent activity</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid={TESTIDS.contractExportCsv}
                onClick={downloadCsv}
                className="btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold"
              >
                <FileDown size={13} strokeWidth={2} /> Export CSV
              </button>
              <button
                data-testid={TESTIDS.contractRefresh}
                onClick={loadList}
                className="btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold"
              >
                <RefreshCw size={13} strokeWidth={2} /> Refresh
              </button>
            </div>
          </div>
          <div className="relative mb-3">
            <Search size={14} strokeWidth={2} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
            <input
              data-testid="contract-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email, name, company or document…"
              className="input-bare !pl-10 !pr-9"
              type="search"
            />
            {search && (
              <button
                data-testid="contract-search-clear"
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-white transition-colors"
              >
                <X size={14} strokeWidth={2} />
              </button>
            )}
          </div>
          {q && !loadingList && (
            <p data-testid="contract-search-count" className="text-[11px] text-[var(--muted)] mb-2">
              {visibleList.length} of {list.length} record{list.length === 1 ? '' : 's'} match &ldquo;{search.trim()}&rdquo;
            </p>
          )}
          <div data-testid={TESTIDS.contractList} className="pnc-card rounded-lg overflow-hidden">
            {loadingList ? (
              <div className="p-8 text-sm text-[var(--muted)]">Loading…</div>
            ) : list.length === 0 ? (
              <div className="p-10 text-center">
                <p className="serif text-2xl mb-1">No contracts sent yet</p>
                <p className="text-sm text-[var(--muted)]">Paste emails to dispatch your first batch.</p>
              </div>
            ) : visibleList.length === 0 ? (
              <div className="p-10 text-center">
                <p className="serif text-2xl mb-1">No matches</p>
                <p className="text-sm text-[var(--muted)]">Nothing sent to &ldquo;{search.trim()}&rdquo; — try a different search.</p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--line)] max-h-[640px] overflow-y-auto">
                {visibleList.map((c) => (
                  <div data-testid={TESTIDS.contractRow} key={c.token} className="p-5 hover:bg-[#161616] transition-colors">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <span className="font-semibold text-sm break-all">{c.employee_email}</span>
                          {c.service_type === 'optout_48h'
                            ? <OptOutPill record={c} />
                            : <StatusPill status={c.status} />}
                        </div>
                        {(c.contractor_name || c.contractor_company) && (
                          <p className="text-xs text-[var(--muted)]">
                            {c.contractor_name ? <>{c.contractor_name} &middot; </> : null}
                            {c.contractor_company || ''}
                          </p>
                        )}
                        <p className="text-[11px] text-[#8A8A8A] mt-1">
                          {c.document_name || SERVICE_LABEL[c.service_type] || 'Contract'} · {c.contract_type === 'unique' ? 'Unique LTD' : 'Smart Solutions'}
                          {' · '}Sent {new Date(c.sent_at).toLocaleString()}
                          {c.signed_at ? <>  &middot; {c.status === 'declined' ? 'Declined' : 'Signed'} {new Date(c.signed_at).toLocaleString()}</> : null}
                          {c.resend_count ? <>  &middot; re-sent ×{c.resend_count}</> : null}
                          {c.batch_id ? <>  &middot; batch {c.batch_id.slice(0,8)}</> : null}
                        </p>
                        {c.ip_address && (
                          <p className="text-[11px] text-[#8A8A8A]">
                            IP {c.ip_address}
                          </p>
                        )}
                        {c.last_error && (
                          <p className="text-[11px] text-rose-400 mt-1 break-words">{c.last_error}</p>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0 items-center">
                        {!['signed', 'declined'].includes(c.status) && (
                          <button
                            data-testid={TESTIDS.contractRowResend}
                            onClick={() => resendInvitation(c)}
                            disabled={resendingToken === c.token}
                            title="Re-send invitation email"
                            className="btn-ghost inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-semibold disabled:opacity-50"
                          >
                            <MailPlus size={12} strokeWidth={2} /> {resendingToken === c.token ? 'Scheduling…' : 'Resend'}
                          </button>
                        )}
                        <button
                          data-testid={TESTIDS.contractRowCopyLink}
                          onClick={() => copyLink(c.token)}
                          className="btn-ghost inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-semibold"
                        >
                          <Copy size={12} strokeWidth={2} /> Copy link
                        </button>
                        {c.signed_pdf_url ? (
                          c.service_type === 'medical' ? (
                            <button
                              data-testid="contract-row-medical-pdf"
                              onClick={() => downloadMedicalPdf(c)}
                              title="Confidential — authorised admin access only"
                              className="btn-ink text-xs px-3 py-1.5 rounded-md font-semibold"
                            >
                              Medical PDF ↗
                            </button>
                          ) : (
                            <a
                              data-testid={TESTIDS.contractRowDownload}
                              href={`${API_BASE}/contracts/sign/${c.token}/signed-pdf`}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-ink text-xs px-3 py-1.5 rounded-md font-semibold"
                            >
                              {c.service_type === 'contract' || c.service_type === 'optout_48h' ? 'Signed PDF ↗' : 'Confirmation ↗'}
                            </a>
                          )
                        ) : null}
                        <button
                          data-testid={TESTIDS.contractRowDelete}
                          onClick={() => setDeleteTarget(c)}
                          title="Delete record"
                          aria-label={`Delete record for ${c.employee_email}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[var(--line)] text-[var(--muted)] hover:text-rose-400 hover:border-rose-500/50 hover:bg-rose-500/10 transition-colors"
                        >
                          <Trash2 size={14} strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
      )}
      </main>

      <DeleteConfirmModal
        target={deleteTarget}
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default function AdminPage() {
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const t = getAdminAuth();
    if (!t) { setChecking(false); return; }
    apiAdmin.get('/admin/me')
      .then((r) => setSession(r.data))
      .catch(() => clearAdminAuth())
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[var(--muted)]">Loading…</div>;
  }
  if (!session) return <LoginScreen onSuccess={(d) => setSession(d)} />;

  return (
    <AdminConsole session={session} onLogout={() => { clearAdminAuth(); setSession(null); navigate('/admin'); }} />
  );
}
